import PropTypes from 'prop-types'
import { Component } from 'react'
import throttle from 'lodash/throttle'
import isEmpty from 'lodash/isEmpty'

import { sendHookRequest } from '@/checkout-instore-legacy/fetchers/HookFetcher'
import { sendDeviceRequest } from '@/checkout-instore-legacy/fetchers/DeviceFetcher'
import { genericImportantError } from '@/checkout-instore-legacy/utils/EventHelper'
import { getNativePlatform } from '@/checkout-instore-legacy/device/utils'
import * as ErrorUtils from '@/checkout-instore-legacy/utils/ErrorUtils'
import * as WebViewBridge from '@/checkout-instore-legacy/utils/WebViewBridge'

import defaultWebHookNotification from './defaultWebHookNotification'

class WebHook extends Component {
  constructor(props) {
    super(props)

    this.state = {
      message: props.sendImmediately ? this.getDefaultMessage(props) : null,
      showMessages: false,
      error: null,
    }

    this.sendHookRequest = throttle(
      this.sendHookRequest,
      props.delayBetweenHooks,
      {
        trailing: false,
      }
    )
  }

  getDefaultMessage(props) {
    return props.hook ? (props.messages && props.messages.default) || '' : null
  }

  sendHookRequest() {
    const { hook, messages, hookCallback, useEventHook } = this.props
    if (!hook || !hook.url) {
      return
    }

    this.setState({ showMessages: true })

    const platform = getNativePlatform()
    if (platform === 'ios') {
      WebViewBridge.sendEvent({
        type: 'sendHookRequest',
        data: {
          hook,
        },
      })
      return
    }

    const sendHookPromise = useEventHook
      ? sendDeviceRequest(hook)
      : sendHookRequest(hook)

    return sendHookPromise
      .then((response) => {
        this.setState({
          message: messages && messages.success,
        })
        hookCallback && hookCallback(null, response)
        return response
      })
      .catch((error) => {
        const errorMessage = !isEmpty(error.response)
          ? ErrorUtils.getErrorMessage(error.response)
          : ErrorUtils.getErrorMessage(error)

        genericImportantError('print-step', {
          context: 'Internal',
          error: errorMessage,
        })

        this.setState({
          error: `${messages && messages.error}: ${errorMessage}`,
        })

        hookCallback && hookCallback(error)

        return Promise.reject(error)
      })
  }

  resendHookRequest() {
    this.setState({
      message: this.getDefaultMessage(this.props),
      error: null,
    })
    this.sendHookRequest()
  }

  componentDidMount() {
    if (this.props.sendImmediately) {
      this.sendHookRequest()
    }
  }

  render() {
    const { message, showMessages, error } = this.state
    const { hook, children, errorNodeSelector } = this.props

    if (!hook || !hook.url || (!message && !error) || !showMessages) {
      return null
    }

    return children({ error, message, errorNodeSelector })
  }
}

WebHook.defaultProps = {
  delayBetweenHooks: 5000,
  sendImmediately: true,
  children: defaultWebHookNotification,
  useEventHook: false,
}

WebHook.propTypes = {
  hook: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
    headers: PropTypes.object,
    data: PropTypes.object,
    retries: PropTypes.number,
  }),
  sendImmediately: PropTypes.bool,
  useEventHook: PropTypes.bool,
  delayBetweenHooks: PropTypes.number,
  hookCallback: PropTypes.func,
  children: PropTypes.func,
  errorNodeSelector: PropTypes.string,
  messages: PropTypes.shape({
    default: PropTypes.string.isRequired,
    success: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
  }).isRequired,
}

export default WebHook
